import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { LogOut, Package, User, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const Account = () => {
  const { user, loading: authLoading, logout } = useAuth();
  const nav = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!authLoading && !user) {
      nav("/login?redirect=/account", { replace: true });
      return;
    }
    if (user) {
      api.get("/customers/orders")
        .then((r) => setOrders(r.data))
        .finally(() => setLoading(false));
    }
  }, [user, authLoading, nav]);

  if (!user) return null;

  const paidOrders = orders.filter((o) => o.payment_status === "paid");
  const totalSpent = paidOrders.reduce((s, o) => s + (o.total || 0), 0);
  const isFirstTimer = paidOrders.length === 0;

  return (
    <main data-testid="account-page" className="container-x py-12 md:py-16">
      <div className="flex items-start justify-between flex-wrap gap-4 mb-10">
        <div>
          <div className="text-[11px] tracking-[0.28em] uppercase text-[#EC008C] font-semibold mb-3">My Account</div>
          <h1 className="font-display font-light text-4xl md:text-5xl text-black tracking-tight">
            Hi, <span className="font-black">{(user.name || user.email.split('@')[0]).split(' ')[0]}.</span>
          </h1>
          <div className="mt-2 text-sm text-[#4A4A4A]">{user.email}</div>
        </div>
        <button
          data-testid="account-logout"
          onClick={() => { logout(); toast.success("Signed out"); nav("/"); }}
          className="btn-outline inline-flex"
        >
          <LogOut size={14} /> Sign out
        </button>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-10">
        <div data-testid="account-stat-orders" className="border border-[#E5E2DA] p-5">
          <Package size={20} className="text-[#EC008C] mb-2" />
          <div className="text-[10px] tracking-[0.24em] uppercase font-semibold text-[#4A4A4A]">Total orders</div>
          <div className="font-display font-black text-3xl text-black mt-1">{orders.length}</div>
        </div>
        <div data-testid="account-stat-spent" className="border border-[#E5E2DA] p-5">
          <User size={20} className="text-[#EC008C] mb-2" />
          <div className="text-[10px] tracking-[0.24em] uppercase font-semibold text-[#4A4A4A]">Total spent</div>
          <div className="font-display font-black text-3xl text-black mt-1">${totalSpent.toFixed(2)}</div>
        </div>
        {isFirstTimer && (
          <div data-testid="account-first-timer-badge" className="border border-[#EC008C] bg-[#FFF0F8] p-5 md:col-span-1">
            <Sparkles size={20} className="text-[#EC008C] mb-2" />
            <div className="text-[10px] tracking-[0.24em] uppercase font-semibold text-[#EC008C]">First-timer perk</div>
            <div className="text-sm font-semibold text-black mt-1">10% off ready at checkout</div>
            <div className="text-xs text-[#4A4A4A] font-light mt-1">Applied automatically</div>
          </div>
        )}
      </div>

      {/* Orders */}
      <div>
        <h2 className="font-display font-bold text-2xl text-black mb-6">Order history</h2>
        {loading ? (
          <div className="text-center py-16"><Loader2 className="animate-spin mx-auto text-[#EC008C]" /></div>
        ) : orders.length === 0 ? (
          <div className="border border-[#E5E2DA] p-12 text-center">
            <Package size={32} className="text-[#EC008C] mx-auto mb-3" />
            <div className="font-semibold text-black">No orders yet</div>
            <p className="text-sm text-[#4A4A4A] font-light mt-1">Once you place your first order it'll show up here.</p>
            <Link to="/shop" data-testid="account-shop-cta" className="mt-5 inline-block btn-primary">
              Start shopping
            </Link>
          </div>
        ) : (
          <div className="space-y-3">
            {orders.map((o) => (
              <div key={o.id} data-testid={`account-order-${o.id}`} className="border border-[#E5E2DA] p-5 flex flex-wrap items-center gap-4">
                <div className="flex-1 min-w-[220px]">
                  <div className="text-[10px] tracking-[0.24em] uppercase text-[#4A4A4A]">Order · {new Date(o.created_at).toLocaleDateString()}</div>
                  <div className="font-semibold text-black mt-1">{o.items.length} item{o.items.length !== 1 ? "s" : ""}</div>
                  <div className="text-xs text-[#4A4A4A] font-light mt-1 line-clamp-1">
                    {o.items.map((i) => i.name).join(", ")}
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-black text-lg text-black">${(o.total || 0).toFixed(2)}</div>
                  {o.discount_code && <div className="text-[10px] uppercase tracking-wider text-[#EC008C] font-semibold">{o.discount_code}</div>}
                </div>
                <span className={`px-3 py-1 text-[10px] tracking-[0.22em] uppercase font-semibold border ${
                  o.payment_status === "paid"
                    ? "bg-[#CCDE00] text-black border-black"
                    : "bg-white text-[#4A4A4A] border-[#E5E2DA]"
                }`}>
                  {o.payment_status}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </main>
  );
};

export default Account;
